import { numberParam, stringParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { roundTo } from '../types/geometry.js';
import { missingRequirementReason, notChecked, plainResult } from './support.js';

const parseHexColor = (value: string): { r: number; g: number; b: number } | null => {
  const match = /^#?([0-9a-f]{6})$/i.exec(value.trim());
  if (!match) return null;
  const hex = match[1] as string;
  return {
    r: Number.parseInt(hex.slice(0, 2), 16),
    g: Number.parseInt(hex.slice(2, 4), 16),
    b: Number.parseInt(hex.slice(4, 6), 16),
  };
};

/**
 * Background check.
 *
 * Aggregates the background metrics into one verdict and — crucially — forwards
 * each localized issue as a problem marker with a normalized rectangle, so the
 * overlay can point at the exact patch of background that is wrong: a shadow on
 * the left, a door frame on the right, a colour cast over everything.
 */
export const backgroundCheck: ValidationCheck = {
  id: 'background',
  title: 'Фон',
  description: 'Проверяет однородность, светлоту и чистоту фона.',
  kind: 'background',
  requires: ['background'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['background']);
    if (missing) return notChecked(ctx, backgroundCheck, missing, ctx.rule.severity);
    const background = ctx.background;
    if (!background) return notChecked(ctx, backgroundCheck, 'Анализ фона недоступен.', ctx.rule.severity);

    const minUniformity = numberParam(ctx.rule.params, 'minUniformity', 0.72);
    const maxShadowScore = numberParam(ctx.rule.params, 'maxShadowScore', 0.35);
    const maxClutterScore = numberParam(ctx.rule.params, 'maxClutterScore', 0.3);
    const maxColorCastScore = numberParam(ctx.rule.params, 'maxColorCastScore', 0.3);
    const maxTargetDeviation = numberParam(ctx.rule.params, 'maxTargetDeviation', 0.25);
    const targetColorHex = stringParam(ctx.rule.params, 'targetColor', '#FFFFFF');
    const targetColor = parseHexColor(targetColorHex) ?? { r: 255, g: 255, b: 255 };

    if (!background.hasMask) {
      return plainResult({
        id: backgroundCheck.id,
        title: backgroundCheck.title,
        status: 'not_checked',
        severity: ctx.rule.severity,
        message:
          'Фон проанализировать не удалось: вокруг головы слишком мало видимого фона или кадр перекрыт.',
        fixability: 'retake',
        details: { metrics: background },
      });
    }

    const errorIssues = background.issues.filter((issue) => issue.severity === 'error');
    const warningIssues = background.issues.filter((issue) => issue.severity === 'warning');

    const checks = {
      uniformity: background.uniformity >= minUniformity,
      shadow: background.shadowScore <= maxShadowScore,
      clutter: background.clutterScore <= maxClutterScore,
      colorCast: background.colorCastScore <= maxColorCastScore,
      target: background.targetDeviation <= maxTargetDeviation,
    };

    const status = errorIssues.length > 0
      ? 'fail'
      : warningIssues.length > 0 || Object.values(checks).some((value) => !value)
        ? 'warning'
        : 'pass';

    const messages =
      background.issues.length > 0
        ? background.issues.slice(0, 3).map((issue) => issue.message)
        : [status === 'pass' ? 'Фон однородный и светлый.' : 'Фон отличается от требуемого.'];

    const problemRegions = background.issues.slice(0, 4).map((issue, index) => ({
      id: `background_issue_${index}`,
      rect: issue.rect,
      message: issue.message,
      severity: issue.severity,
    }));

    return plainResult({
      id: backgroundCheck.id,
      title: backgroundCheck.title,
      status,
      severity: ctx.rule.severity,
      value: roundTo(background.uniformity, 3),
      min: minUniformity,
      unit: 'ratio',
      message:
        status === 'pass'
          ? 'Фон однородный, светлый, без посторонних предметов и теней.'
          : `Требования к фону: ${messages.join(' ')}`,
      requirement: `Фон однородный, светлый (в идеале белый или почти белый), без теней и посторонних предметов. Целевой цвет: ${targetColorHex}.`,
      fixability: 'retake',
      geometry: [
        {
          type: 'rect',
          coordinates: [
            roundTo(background.subjectRect.x, 4),
            roundTo(background.subjectRect.y, 4),
            roundTo(background.subjectRect.width, 4),
            roundTo(background.subjectRect.height, 4),
          ],
        },
      ],
      problemRegions,
      confidence: 0.6,
      details: {
        uniformity: background.uniformity,
        edgeDensity: background.edgeDensity,
        clutterScore: background.clutterScore,
        shadowScore: background.shadowScore,
        colorCastScore: background.colorCastScore,
        targetDeviation: background.targetDeviation,
        meanColor: background.meanColor,
        meanLuma: background.meanLuma,
        cornerLumaSpread: background.cornerLumaSpread,
        maskCoverage: background.maskCoverage,
        checks,
        targetColor,
        displayValue:
          status === 'pass'
            ? 'однородный'
            : (background.issues[0]?.message ?? 'есть замечания'),
      },
    });
  },
};
